import axios from 'axios';

// Восстанавливаем использование переменной окружения REACT_APP_API_URL
const API_URL = process.env.REACT_APP_API_URL;

// Создаем экземпляр axios с базовым URL
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor для добавления токена к каждому запросу
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Добавляем логирование каждого запроса
    console.log(`Отправляется запрос: ${config.method.toUpperCase()} ${config.baseURL}${config.url}`);
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor для обработки ошибок авторизации
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      // Если сервер вернул 401, значит токен истек или недействителен
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API для авторизации
export const authAPI = {
  // Авторизация через Telegram
  loginWithTelegram: (telegramData) => {
    // Преобразуем данные в строку запроса для GET-запроса
    const queryParams = new URLSearchParams();
    for (const key in telegramData) {
      queryParams.append(key, telegramData[key]);
    }
    return api.get(`/auth/telegram?${queryParams.toString()}`);
  },
  
  // Проверка авторизации
  checkAuth: () => {
    return api.get('/auth/check');
  },
  
  // Выход из системы
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  },
};

// API для работы с чатами
export const chatsAPI = {
  // Получение списка чатов
  getChats: async (forceRefresh = false) => {
    try {
      console.log(`API: Запрос списка чатов (forceRefresh=${forceRefresh})`);
      let url = '/chats';
      
      // Добавляем параметр для принудительного обновления кеша
      if (forceRefresh) {
        url += '?refresh=true';
      }
      
      const response = await api.get(url);
      console.log('API: Получен ответ с чатами:', response.data);
      return response;
    } catch (error) {
      console.error('API: Ошибка получения списка чатов:', error);
      
      // В режиме разработки возвращаем тестовые данные
      if (process.env.NODE_ENV === 'development') {
        console.warn('API: Используем тестовые данные для разработки');
        return {
          data: {
            chats: [
              {
                id: 1000001,
                title: 'Тестовый чат #1',
                type: 'supergroup'
              },
              {
                id: 1000002,
                title: 'Тестовый чат #2',
                type: 'group'
              }
            ]
          }
        };
      }
      
      throw error;
    }
  },
  
  // Принудительное обновление списка чатов
  refreshChats: async () => {
    try {
      console.log('API: Принудительное обновление кеша чатов');
      const response = await api.get('/chats/refresh');
      console.log('API: Результат обновления кеша:', response.data);
      return response;
    } catch (error) {
      console.error('API: Ошибка при обновлении кеша чатов:', error);
      throw error;
    }
  },
  
  // Принудительный сброс кеша сервера
  forceRefresh: async () => {
    try {
      console.log('API: Принудительный сброс кеша сервера');
      const response = await api.get('/force-refresh');
      console.log('API: Результат сброса кеша сервера:', response.data);
      
      // После сброса кеша на сервере, обновляем список чатов
      const chatsResponse = await api.get('/chats/refresh');
      console.log('API: Результат обновления списка чатов:', chatsResponse.data);
      
      return {
        forceRefresh: response.data,
        refreshChats: chatsResponse.data
      };
    } catch (error) {
      console.error('API: Ошибка при сбросе кеша сервера:', error);
      throw error;
    }
  },
  
  // Проверка статуса администратора
  checkAdminStatus: async (chatId = null) => {
    try {
      console.log('API: Проверка статуса администратора');
      const url = chatId ? `/api/admin-status?chat_id=${chatId}` : '/api/admin-status';
      const response = await api.get(url);
      console.log('API: Результат проверки статуса администратора:', response.data);
      return response;
    } catch (error) {
      console.error('API: Ошибка при проверке статуса администратора:', error);
      throw error;
    }
  },
  
  // Публичный метод для получения отладочной информации (без авторизации)
  getDebugInfo: async () => {
    try {
      console.log('API: Запрос отладочной информации');
      // Используем api.defaults для обхода перехватчика авторизации
      const response = await axios.get('/api/debug-status');
      console.log('API: Получена отладочная информация:', response.data);
      return response;
    } catch (error) {
      console.error('API: Ошибка получения отладочной информации:', error);
      throw error;
    }
  },
  
  // Получение конфигурации чата
  getChatConfig: async (chatId) => {
    try {
      console.log(`API: Запрос конфигурации чата ${chatId}`);
      
      // Добавляем случайный параметр для избежания кеширования
      const timestamp = new Date().getTime();
      const response = await api.get(`/chats/${chatId}/config?_t=${timestamp}`);
      
      console.log('API: Получена конфигурация чата:', response.data);
      
      // Нормализуем булевые значения в конфигурации
      if (response.data && response.data.config) {
        const config = response.data.config;
        
        // Обходим все модули и нормализуем булевые значения
        for (const moduleName in config) {
          const moduleConfig = config[moduleName];
          
          // Нормализуем булевые значения
          if ('active' in moduleConfig) {
            if (typeof moduleConfig.active === 'string') {
              moduleConfig.active = moduleConfig.active.toLowerCase() === 'true' || 
                                   moduleConfig.active === '1' ||
                                   moduleConfig.active.toLowerCase() === 'yes';
            } else {
              moduleConfig.active = !!moduleConfig.active;
            }
          }
          
          if ('notification' in moduleConfig) {
            if (typeof moduleConfig.notification === 'string') {
              moduleConfig.notification = moduleConfig.notification.toLowerCase() === 'true' || 
                                        moduleConfig.notification === '1' ||
                                        moduleConfig.notification.toLowerCase() === 'yes';
            } else {
              moduleConfig.notification = !!moduleConfig.notification;
            }
          }
        }
      }
      
      return response;
    } catch (error) {
      console.error(`API: Ошибка получения конфигурации чата ${chatId}:`, error);
      
      // В режиме разработки возвращаем тестовые данные
      if (process.env.NODE_ENV === 'development') {
        console.warn('API: Используем тестовые данные конфигурации');
        return {
          data: {
            config: {
              system: {
                enabled: true,
                admins: []
              },
              grep: {
                enabled: true,
                words: ["тестовое слово"]
              }
            }
          }
        };
      }
      
      throw error;
    }
  },
  
  // Обновление конфигурации чата
  updateChatConfig: (chatId, config) => {
    return api.put(`/chats/${chatId}/config`, { config });
  },
};

// API для работы с модулями
export const modulesAPI = {
  // Получение списка модулей
  getModules: () => {
    return api.get('/modules');
  },
  
  // Получение настроек модуля
  getModuleSettings: async (chatId, moduleName) => {
    try {
      console.log(`modulesAPI: Запрос настроек модуля ${moduleName} для чата ${chatId}`);
      
      // Добавляем случайный параметр, чтобы избежать кеширования браузером
      const timestamp = new Date().getTime();
      const response = await api.get(`/chats/${chatId}/modules/${moduleName}/settings?_t=${timestamp}`);
      
      console.log(`modulesAPI: Получен ответ:`, response.data);
      
      // Проверяем, есть ли в ответе настройки
      if (!response.data || !response.data.settings) {
        console.error(`modulesAPI: В ответе отсутствуют настройки:`, response.data);
        // Создаем базовые настройки
        return {
          data: {
            settings: {
              active: false,
              notification: true
            }
          }
        };
      }
      
      // Нормализуем типы данных во избежание проблем с отображением в UI
      const normalizedSettings = { ...response.data.settings };
      
      // Явно преобразуем 'active' к булевому типу
      if ('active' in normalizedSettings) {
        if (typeof normalizedSettings.active === 'string') {
          normalizedSettings.active = normalizedSettings.active.toLowerCase() === 'true' || 
                                     normalizedSettings.active === '1' ||
                                     normalizedSettings.active.toLowerCase() === 'yes';
        } else {
          normalizedSettings.active = !!normalizedSettings.active;
        }
        console.log(`modulesAPI: Нормализовано значение active: ${normalizedSettings.active} (${typeof normalizedSettings.active})`);
      }
      
      // Явно преобразуем 'notification' к булевому типу
      if ('notification' in normalizedSettings) {
        if (typeof normalizedSettings.notification === 'string') {
          normalizedSettings.notification = normalizedSettings.notification.toLowerCase() === 'true' || 
                                          normalizedSettings.notification === '1' ||
                                          normalizedSettings.notification.toLowerCase() === 'yes';
        } else {
          normalizedSettings.notification = !!normalizedSettings.notification;
        }
      }
      
      return {
        data: {
          settings: normalizedSettings
        }
      };
    } catch (error) {
      console.error(`modulesAPI: Ошибка получения настроек модуля ${moduleName}:`, error);
      // В случае ошибки возвращаем объект с базовыми настройками
      return {
        data: {
          settings: {
            active: false,
            notification: true
          },
          error: error.message
        }
      };
    }
  },
  
  // Обновление настроек модуля
  updateModuleSettings: async (chatId, moduleName, settings) => {
    try {
      console.log(`modulesAPI: Обновление настроек модуля ${moduleName} для чата ${chatId}`, settings);
      
      // Создаем копию настроек для нормализации
      const normalizedSettings = { ...settings };
      
      // Нормализуем значения перед отправкой
      if ('active' in normalizedSettings) {
        if (typeof normalizedSettings.active === 'string') {
          normalizedSettings.active = normalizedSettings.active.toLowerCase() === 'true' || 
                                    normalizedSettings.active === '1' ||
                                    normalizedSettings.active.toLowerCase() === 'yes';
        } else {
          normalizedSettings.active = !!normalizedSettings.active;
        }
        console.log(`modulesAPI: Нормализованное значение active для отправки: ${normalizedSettings.active} (${typeof normalizedSettings.active})`);
      }
      
      if ('notification' in normalizedSettings) {
        // Проверяем и отображаем исходное значение для отладки
        console.log(`modulesAPI: Исходное значение notification перед нормализацией: ${normalizedSettings.notification} (${typeof normalizedSettings.notification})`);
        
        if (typeof normalizedSettings.notification === 'string') {
          normalizedSettings.notification = normalizedSettings.notification.toLowerCase() === 'true' || 
                                          normalizedSettings.notification === '1' ||
                                          normalizedSettings.notification.toLowerCase() === 'yes';
        } else {
          normalizedSettings.notification = !!normalizedSettings.notification;
        }
        console.log(`modulesAPI: Нормализованное значение notification для отправки: ${normalizedSettings.notification} (${typeof normalizedSettings.notification})`);
      }
      
      // Добавляем случайный параметр для избежания кеширования
      const timestamp = new Date().getTime();
      
      // Проверка на изменение активности
      let shouldForceStatus = false;
      if ('active' in normalizedSettings && moduleName === 'watchdog') {
        shouldForceStatus = true;
        console.log(`modulesAPI: Для модуля watchdog будет использован force-status`);
      }
      
      // Если есть изменение в активности watchdog модуля, используем force-метод
      if (shouldForceStatus && 'active' in normalizedSettings) {
        try {
          console.log(`modulesAPI: Попытка принудительной установки статуса модуля ${moduleName} на ${normalizedSettings.active}`);
          // Сначала принудительно устанавливаем статус активности напрямую
          const forceResponse = await api.put(
            `/chats/${chatId}/modules/${moduleName}/force-status?_t=${timestamp}`, 
            { active: normalizedSettings.active }
          );
          console.log(`modulesAPI: Статус активности модуля ${moduleName} принудительно установлен на ${normalizedSettings.active}`, forceResponse.data);
        } catch (forceError) {
          console.error(`modulesAPI: Ошибка при принудительной установке статуса: `, forceError);
          // Продолжаем выполнение, даже если принудительная установка не удалась
        }
      }
      
      // Отправляем нормализованные настройки
      console.log(`modulesAPI: Отправка обновленных настроек на сервер:`, normalizedSettings);
      const response = await api.put(`/chats/${chatId}/modules/${moduleName}/settings?_t=${timestamp}`, {
        settings: normalizedSettings
      });
      
      console.log(`modulesAPI: Получен ответ от сервера:`, response.data);
      
      // Возвращаем нормализованные данные из ответа
      if (response.data && response.data.settings) {
        const responseSettings = { ...response.data.settings };
        // Нормализуем значения в ответе
        if ('active' in responseSettings) {
          if (typeof responseSettings.active === 'string') {
            responseSettings.active = responseSettings.active.toLowerCase() === 'true' || 
                                     responseSettings.active === '1' ||
                                     responseSettings.active.toLowerCase() === 'yes';
          } else {
            responseSettings.active = !!responseSettings.active;
          }
          console.log(`modulesAPI: Нормализованное значение active в ответе: ${responseSettings.active} (${typeof responseSettings.active})`);
        }
        
        if ('notification' in responseSettings) {
          console.log(`modulesAPI: Исходное значение notification в ответе: ${responseSettings.notification} (${typeof responseSettings.notification})`);
          
          if (typeof responseSettings.notification === 'string') {
            responseSettings.notification = responseSettings.notification.toLowerCase() === 'true' || 
                                           responseSettings.notification === '1' ||
                                           responseSettings.notification.toLowerCase() === 'yes';
          } else {
            responseSettings.notification = !!responseSettings.notification;
          }
          console.log(`modulesAPI: Нормализованное значение notification в ответе: ${responseSettings.notification} (${typeof responseSettings.notification})`);
        }
        
        return {
          data: {
            ...response.data,
            settings: responseSettings
          }
        };
      }
      
      return response;
    } catch (error) {
      console.error(`modulesAPI: Ошибка обновления настроек модуля ${moduleName}:`, error);
      throw error;
    }
  },
  
  // Пакетное обновление настроек модулей
  batchUpdateModules: async (chatId, modules) => {
    try {
      console.log(`modulesAPI: Пакетное обновление модулей для чата ${chatId}`, modules);
      
      // Создаем копию модулей для нормализации
      const normalizedModules = { ...modules };
      
      // Нормализуем значения перед отправкой
      for (const moduleName in normalizedModules) {
        const moduleSettings = normalizedModules[moduleName];
        
        if ('active' in moduleSettings) {
          if (typeof moduleSettings.active === 'string') {
            moduleSettings.active = moduleSettings.active.toLowerCase() === 'true' || 
                                  moduleSettings.active === '1' ||
                                  moduleSettings.active.toLowerCase() === 'yes';
          } else {
            moduleSettings.active = !!moduleSettings.active;
          }
        }
        
        if ('notification' in moduleSettings) {
          if (typeof moduleSettings.notification === 'string') {
            moduleSettings.notification = moduleSettings.notification.toLowerCase() === 'true' || 
                                       moduleSettings.notification === '1' ||
                                       moduleSettings.notification.toLowerCase() === 'yes';
          } else {
            moduleSettings.notification = !!moduleSettings.notification;
          }
        }
      }
      
      // Добавляем случайный параметр для избежания кеширования
      const timestamp = new Date().getTime();
      
      // Отправляем нормализованные настройки
      const response = await api.put(`/chats/${chatId}/modules/batch-update?_t=${timestamp}`, {
        modules: normalizedModules
      });
      
      return response;
    } catch (error) {
      console.error(`modulesAPI: Ошибка пакетного обновления модулей:`, error);
      throw error;
    }
  },
  
  // Принудительная установка статуса модуля (обходя стандартные механизмы)
  forceModuleStatus: async (chatId, moduleName, active) => {
    try {
      console.log(`modulesAPI: Принудительная установка статуса для модуля ${moduleName} в чате ${chatId} на ${active}`);
      
      // Нормализуем значение
      let normalizedActive = active;
      if (typeof active === 'string') {
        normalizedActive = active.toLowerCase() === 'true' || 
                        active === '1' ||
                        active.toLowerCase() === 'yes';
      } else {
        normalizedActive = !!active;
      }
      
      // Добавляем случайный параметр для избежания кеширования
      const timestamp = new Date().getTime();
      
      // Отправляем запрос на принудительное обновление
      const response = await api.put(`/chats/${chatId}/modules/${moduleName}/force-status?_t=${timestamp}`, {
        active: normalizedActive
      });
      
      console.log(`modulesAPI: Результат принудительной установки статуса:`, response.data);
      
      return response;
    } catch (error) {
      console.error(`modulesAPI: Ошибка принудительной установки статуса модуля ${moduleName}:`, error);
      throw error;
    }
  }
};

export default api; 